import moment from "moment";
import React from "react";
import Chart from "react-apexcharts";
import { usePriceFormatter } from "../../../hooks";

type LineChartProps = {
  data?: { x: string | Date; y: number }[];
  width?: string | number;
  height?: string | number;
  lineColor?: string;
};

const LinechartMiniPortfolio: React.FC<LineChartProps> = ({
  data,
  width = "100%",
  height = "400px",
  lineColor = "#4edf6d"
}) => {
  const validatedData = data || [];

  const formatter = usePriceFormatter();

  // Handle single data point by duplicating it
  const chartData = validatedData.length === 1
    ? [validatedData[0], validatedData[0]]
    : validatedData;

  const options: ApexCharts.ApexOptions = {
    chart: {
      id: "basic-line",
      background: "transparent",
      toolbar: { show: false },
      zoom: { enabled: false }
    },
    xaxis: {
      type: "datetime",
      labels: { show: false, style: { colors: 'white' } },
      axisBorder: { show: false },
      axisTicks: { show: false },
      tooltip: { enabled: false },
    },
    yaxis: {
      axisBorder: {
        show: false,
        color: "#ccc",
        width: 1,
      },
      labels: { show: false, style: { colors: 'white' } },
    },
    stroke: {
      curve: "smooth",
      width: validatedData.length === 1 ? 0 : 2, // Hide line for single point
      colors: [lineColor],
    },
    tooltip: {
      enabled: true,
      custom: ({ series, seriesIndex, dataPointIndex, w }) => {
        const price = series[seriesIndex][dataPointIndex];
        const time = new Date(
          w.globals.seriesX[seriesIndex][dataPointIndex]
        ).toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
        });
        const date = moment(w.globals.seriesX[seriesIndex][dataPointIndex]).format("MM-DD-YYYY");
        return `<div style="background-color: #333; display: flex; gap: 5px; color: #fff; padding: 5px; border-radius: 5px;">
                  <span>${formatter.display(price)}</span>
                  <span>|</span>
                  <span>${date}</span>
                </div>`;
      },
    },
    grid: { show: false },
    markers: {
      size: validatedData.length === 1 ? 8 : 0, // Show larger marker for single point
      colors: ["#FFFFFF"],
      strokeWidth: validatedData.length === 1 ? 3 : 5,
      strokeColors: lineColor,
      hover: { size: validatedData.length === 1 ? 10 : 7 },
    },
    // Add annotations for single point to show it's not a trend
    ...(validatedData.length === 1 && {
      annotations: {
        points: [{
          x: new Date(validatedData[0].x).getTime(),
          y: validatedData[0].y,
          marker: {
            size: 8,
            fillColor: lineColor,
            strokeColor: "#fff",
            strokeWidth: 3,
          }
        }]
      }
    })
  };

  const series: ApexAxisChartSeries = [
    {
      name: "Price",
      data: chartData,
    },
  ];

  // Show message overlay for single point
  const showSinglePointMessage = validatedData.length === 1;

  return (
    <div style={{ position: "relative" }}>
      <Chart
        options={options}
        series={series}
        type="line"
        width={width}
        height={height}
      />
      {showSinglePointMessage && (
        <div style={{
          position: "absolute",
          bottom: "20px",
          left: "50%",
          transform: "translateX(-50%)",
          color: "#888",
          fontSize: "12px",
          textAlign: "center",
          padding: "4px 8px",
          backgroundColor: "rgba(0,0,0,0.5)",
          borderRadius: "4px"
        }}>
          Current snapshot • Historical data will appear as it accumulates
        </div>
      )}
    </div>
  );
};

export default LinechartMiniPortfolio;